import { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Building2, DollarSign, TrendingUp, AlertCircle, Users, Percent,
  Sparkles, RefreshCw,
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// -----------------------------------------------------------------------------
// Small helpers
// -----------------------------------------------------------------------------
const fmtMoney = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;
const monthLabel = (mkey) => {
  if (!mkey || mkey.length < 7) return "";
  const d = new Date(`${mkey}-01T00:00:00Z`);
  return d.toLocaleString(undefined, { month: "short" });
};

function KPI({ label, value, icon: Icon, tone = "neutral", testid }) {
  const toneMap = {
    neutral: "border-white/10",
    ok: "border-emerald-500/30 bg-emerald-500/[0.04]",
    warn: "border-amber-500/30 bg-amber-500/[0.04]",
    danger: "border-red-500/30 bg-red-500/[0.04]",
    info: "border-orange-500/30 bg-orange-500/[0.04]",
  };
  const iconTone = {
    neutral: "text-zinc-400",
    ok: "text-emerald-400",
    warn: "text-amber-400",
    danger: "text-red-400",
    info: "text-orange-400",
  };
  return (
    <Card className={`p-4 ${toneMap[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
        <Icon size={12} className={iconTone[tone]} /> {label}
      </div>
      <div className="text-2xl font-semibold text-white mt-1 font-display">{value}</div>
    </Card>
  );
}

// Tiny inline bar chart — no external chart lib dependency. Renders 6 month
// bars with a hover tooltip for the exact figure.
function TrendBars({ data }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map((d) => d.collected), 1);
  return (
    <div className="flex items-end gap-3 h-32 mt-4" data-testid="portfolio-trend">
      {data.map((d) => {
        const heightPct = Math.max(4, (d.collected / max) * 100);
        return (
          <div key={d.month} className="flex flex-col items-center flex-1 group">
            <div
              className="w-full bg-orange-500/40 rounded-t border border-orange-500/40 hover:bg-orange-500 transition"
              style={{ height: `${heightPct}%` }}
              title={`${monthLabel(d.month)} · ${fmtMoney(d.collected)}`}
            />
            <div className="text-[10px] text-zinc-500 mt-1">{monthLabel(d.month)}</div>
          </div>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
export default function PortfolioPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/portfolio/rollup");
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load portfolio rollup");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seedStarterVendors = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/vendors/seed-starter-pack");
      toast.success(
        `Seeded ${data.created} vendor${data.created === 1 ? "" : "s"}${
          data.already_existed ? ` (${data.already_existed} already existed)` : ""
        }. Assigned to ${data.pm_assignments} PM schedule${data.pm_assignments === 1 ? "" : "s"}.`
      );
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to seed starter vendors");
    } finally {
      setSeeding(false);
    }
  };

  if (loading && !data) {
    return <div className="text-sm text-zinc-400" data-testid="portfolio-loading">Loading portfolio…</div>;
  }
  const k = data?.kpis || {};
  const buckets = data?.delinquency_by_bucket || {};

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Portfolio"
        title="Portfolio Rollup"
        subtitle="NOI, occupancy, and delinquency across every property you manage."
        right={
          <div className="flex items-center gap-2">
            <Button
              variant="outline" className="border-white/10"
              onClick={load} data-testid="portfolio-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
            <Button
              className="bg-orange-500 hover:bg-orange-600 rounded-full"
              onClick={seedStarterVendors} disabled={seeding}
              data-testid="seed-starter-vendors"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1" />
              {seeding ? "Seeding…" : "Seed starter vendors"}
            </Button>
          </div>
        }
      />

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Properties" value={k.property_count || 0}
             icon={Building2} testid="kpi-properties" />
        <KPI label="Total units" value={(k.total_units || 0).toLocaleString()}
             icon={Users} testid="kpi-units" />
        <KPI label="Occupancy" value={fmtPct(k.occupancy_pct)}
             icon={Percent} tone={k.occupancy_pct >= 90 ? "ok" : k.occupancy_pct >= 75 ? "info" : "warn"}
             testid="kpi-occupancy" />
        <KPI label="Monthly rent roll" value={fmtMoney(k.monthly_rent_roll)}
             icon={DollarSign} tone="info" testid="kpi-rent-roll" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Collected YTD" value={fmtMoney(k.collected_ytd)}
             icon={TrendingUp} tone="ok" testid="kpi-collected" />
        <KPI label="Expenses YTD" value={fmtMoney(k.expenses_ytd)}
             icon={DollarSign} tone="warn" testid="kpi-expenses" />
        <KPI label="Net operating income (YTD)" value={fmtMoney(k.noi_ytd)}
             icon={TrendingUp}
             tone={k.noi_ytd >= 0 ? "ok" : "danger"}
             testid="kpi-noi" />
        <KPI label="Total delinquency" value={fmtMoney(k.delinquency_total)}
             icon={AlertCircle}
             tone={k.delinquency_total > 0 ? "danger" : "neutral"}
             testid="kpi-delinquency" />
      </div>

      {/* Delinquency aging + collection trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border-white/10 bg-card">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Delinquency aging</div>
          {["current", "30_60", "60_90", "90_plus"].map((b) => {
            const amt = buckets[b] || 0;
            const total = Object.values(buckets).reduce((a, v) => a + v, 0) || 1;
            const pct = (amt / total) * 100;
            const tone = b === "current" ? "bg-emerald-500/60"
              : b === "30_60" ? "bg-amber-500/60"
              : b === "60_90" ? "bg-orange-500/70"
              : "bg-red-500/70";
            const label = b === "current" ? "Current"
              : b === "30_60" ? "30–60 days"
              : b === "60_90" ? "60–90 days"
              : "90+ days";
            return (
              <div key={b} className="mb-2 last:mb-0" data-testid={`aging-${b}`}>
                <div className="flex justify-between text-xs text-zinc-300 mb-1">
                  <span>{label}</span>
                  <span className="font-mono">{fmtMoney(amt)}</span>
                </div>
                <div className="h-2 rounded bg-white/[0.04] overflow-hidden">
                  <div className={`${tone} h-full`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </Card>
        <Card className="p-5 border-white/10 bg-card">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Collected · trailing 6 months</div>
          <div className="text-sm text-zinc-400">
            Total <span className="font-mono text-white">
              {fmtMoney((data?.monthly_trend || []).reduce((a, m) => a + m.collected, 0))}
            </span> collected across the portfolio in the last 6 months.
          </div>
          <TrendBars data={data?.monthly_trend || []} />
        </Card>
      </div>

      {/* Per-property table */}
      <Card className="border-white/10 bg-card overflow-hidden">
        <div className="p-3 border-b border-white/10 text-xs uppercase tracking-widest text-zinc-500 flex items-center justify-between">
          <span>Per-property breakdown</span>
          <span className="text-zinc-400">{data?.properties?.length || 0} properties</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Property</TableHead>
              <TableHead className="text-right">Units</TableHead>
              <TableHead className="text-right">Residents</TableHead>
              <TableHead className="text-right">Occupancy</TableHead>
              <TableHead className="text-right">Monthly rent roll</TableHead>
              <TableHead className="text-right">Collected YTD</TableHead>
              <TableHead className="text-right">Delinquency</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.properties || []).length === 0 && (
              <TableRow className="border-white/5">
                <TableCell colSpan={7} className="text-center text-sm text-zinc-500 py-8" data-testid="portfolio-empty">
                  No properties in your portfolio yet.
                </TableCell>
              </TableRow>
            )}
            {(data?.properties || []).map((p) => (
              <TableRow key={p.property_id} className="border-white/5" data-testid={`portfolio-row-${p.property_id}`}>
                <TableCell className="font-medium text-zinc-100">{p.name}</TableCell>
                <TableCell className="text-right font-mono">{p.units}</TableCell>
                <TableCell className="text-right font-mono">{p.residents}</TableCell>
                <TableCell className="text-right font-mono">
                  <Badge variant="outline" className={
                    p.occupancy_pct >= 90 ? "border-emerald-500/40 text-emerald-300"
                    : p.occupancy_pct >= 75 ? "border-orange-500/40 text-orange-300"
                    : "border-amber-500/40 text-amber-300"
                  }>
                    {fmtPct(p.occupancy_pct)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono text-zinc-200">{fmtMoney(p.monthly_rent_roll)}</TableCell>
                <TableCell className="text-right font-mono text-emerald-300">{fmtMoney(p.collected_ytd)}</TableCell>
                <TableCell className="text-right font-mono">
                  <span className={p.delinquency > 0 ? "text-red-300" : "text-zinc-400"}>
                    {fmtMoney(p.delinquency)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
